import type { TabCountTone } from '@repo/ui/components/molecules/data-table-filter-tabs';

/**
 * Tab di una tabella che filtra per un campo di dominio (`level`, `status`,
 * `isActive`). Nell'URL il tab viaggia su `subTab`, come nella webapp; il
 * filtro per l'API si ricava dal tab e `subTab` non va all'API, che rifiuta
 * i parametri sconosciuti con 400.
 */
export interface DomainFilterTab<TFilter> {
  value: string;
  /** Valore del filtro di dominio; assente = il tab "tutti". */
  filter?: TFilter;
}

interface ResolveDomainTabOptions<TFilter> {
  tabs: readonly DomainFilterTab<TFilter>[];
  /** `subTab` dell'URL. */
  subTab: unknown;
  defaultTab: string;
}

interface ResolvedDomainTab<TFilter> {
  activeTab: string;
  filter: TFilter | undefined;
}

export function resolveDomainTab<TFilter>({
  tabs,
  subTab,
  defaultTab,
}: ResolveDomainTabOptions<TFilter>): ResolvedDomainTab<TFilter> {
  const active =
    tabs.find((tab) => tab.value === subTab) ??
    tabs.find((tab) => tab.value === defaultTab) ??
    tabs[0];

  return { activeTab: active?.value ?? defaultTab, filter: active?.filter };
}

interface LegacySubTabOptions<TFilter> {
  tabs: readonly DomainFilterTab<TFilter>[];
  subTab: string | null | undefined;
  /** Il parametro di dominio di un link vecchio (`?status=...`). */
  legacyFilter: unknown;
}

/**
 * Il `subTab` dell'URL se è uno dei tab della lista, altrimenti nessuno: un
 * valore scritto a mano (`?subTab=archiviati`) non deve lasciare la barra dei
 * tab senza un tab acceso mentre la lista mostra quello di default.
 */
export function toKnownSubTab(
  tabs: readonly { value: string }[],
  subTab: string | null | undefined
): string | undefined {
  return tabs.some((tab) => tab.value === subTab)
    ? (subTab ?? undefined)
    : undefined;
}

/**
 * Link vecchi (dashboard, email, segnalibri) con il solo parametro di
 * dominio: lo schema della rotta lo traduce una volta in `subTab`, e il
 * parametro vecchio non esce più dalla search. Un `subTab` valido vince.
 *
 * Da rimuovere dopo il 2026-12-31, con i parametri vecchi degli schemi.
 */
export function toLegacySubTab<TFilter>({
  tabs,
  subTab,
  legacyFilter,
}: LegacySubTabOptions<TFilter>): string | null | undefined {
  if (tabs.some((tab) => tab.value === subTab)) return subTab;
  if (legacyFilter === undefined || legacyFilter === null) return subTab;
  return (
    tabs.find((tab) => tab.filter !== undefined && tab.filter === legacyFilter)
      ?.value ?? subTab
  );
}

/**
 * Tab di dominio con l'etichetta come chiave i18n. `countTone` è il tono della
 * pill del contatore (Previxio DS · Tabs): si dichiara qui, accanto al tab, e
 * la pill compare solo quando `withTabCounts` gli dà un conteggio.
 */
export type TranslatableDomainTab<TFilter> = DomainFilterTab<TFilter> & {
  text: string;
  countTone?: TabCountTone;
};

export const ACTIVE_STATUS_DEFAULT_TAB = 'all';

/**
 * Tab dello stato attivo (Tutti / Attivi / Inattivi), uguali in ogni lista
 * che filtra per `isActive`: il tab "tutti" non fissa il filtro.
 */
export const ACTIVE_STATUS_TABS: readonly TranslatableDomainTab<boolean>[] = [
  { value: ACTIVE_STATUS_DEFAULT_TAB, text: 'commons.all' },
  { value: 'active', text: 'commons.status_tabs.active', filter: true },
  { value: 'inactive', text: 'commons.status_tabs.inactive', filter: false },
];

interface TranslatedFilterTab {
  value: string;
  text: string;
  countTone?: TabCountTone;
}

/** I tab per la tabella (`filterTabs`), con l'etichetta tradotta e il tono del contatore. */
export function translateFilterTabs(
  tabs: readonly { value: string; text: string; countTone?: TabCountTone }[],
  t: (key: string) => string
): TranslatedFilterTab[] {
  return tabs.map(({ value, text, countTone }) => ({
    value,
    text: t(text),
    ...(countTone && { countTone }),
  }));
}

/** Conteggi dei tab per `value`; un tab assente dalla mappa non ha la pill. */
export type TabCounts = Partial<Record<string, number>>;

/**
 * Aggiunge ai tab il contatore (`count`) dalla mappa per `value`. Senza mappa
 * (conteggi ancora in caricamento) nessun tab ha la pill, invece di uno 0 che
 * diventa il numero vero un attimo dopo. Nemmeno lo zero ha la pill: in
 * tutti i moduli un tab senza elementi non mostra «0» (un «Errori 0» rosso
 * allarmava per niente).
 */
export function withTabCounts<TTab extends { value: string }>(
  tabs: readonly TTab[],
  counts: TabCounts | undefined
): (TTab & { count?: number })[] {
  return tabs.map((tab) => {
    const count = counts?.[tab.value];
    return count === undefined || count === 0 ? { ...tab } : { ...tab, count };
  });
}
